import {
  ContentfulCarousel,
  ContentfulCarouselDisplayInfo,
  ContentfulCarouselType,
  ContentfulProductSource,
} from '@jbhi-fi/apis/src/contentful/types';
import { AssetTransformer } from '../AssetTransformer';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { AlignedButton } from '../../../AlignedButton/AlignedButton';
import { ButtonTransformer, LanyardButtonProps } from '../ButtonTransformer';
import { CardTransformer } from '../CardTransformer';
import { UserProfile } from '../../../UserProfile/UserProfile';
import { AuthorTransformer } from '../AuthorTransformer';
import { CardGridCardProps } from '../../../ContentCard/CardGrid/CardGrid';
import { DynamicCarouselCover } from '../../../CarouselPresentationContainer/DynamicCarouselCover';
import { buildContentfulApiImgSrc } from '../../../ContentfulImage/buildContentfulApiImgSrc';
import React, { Fragment } from 'react';
import { PresentationVideoCardCarousel, PresentationVideoCardCarouselProps } from '../../../ContentCard';
import {
  PresentationProductCarousel,
  PresentationProductCarouselProps,
  ProductCarousel,
  ProductCarouselProps,
} from '../../../ProductCarousel/ProductCarousel';
import { MiniProductTile } from '../../../MiniProductTile/MiniProductTile';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { CarouselLeadBrand, CarouselLeadBrandProps } from '../../../CarouselLeadBrand/CarouselLeadBrand';
import { ContentfulImage } from '../../../ContentfulImage/ContentfulImage';
import { CarouselLeadImage, CarouselLeadImageProps } from '../../../CarouselLeadImage/CarouselLeadImage';
import { ListingBlock, ListingBlockProps } from '../../../ListingBlock';
import { AlgoliaHitTransformer, MiniProductInfoVariant } from '../../../Algolia/transformers/AlgoliaHitTransformer';
import { CarouselLeadVideo, CarouselLeadVideoProps } from '../../../CarouselLeadVideo/CarouselLeadVideo';
import { ContentBlockProps } from '../../../ContentBlock';
import { Atoms } from '../../../styles/atoms.css';
import Button from '@jbhi-fi/lanyard-ui/components/Button/Button';
import { ContentEntry } from '../../../ContentEntry/contentEntries';
import { CollectionTransformer } from '../CollectionTransformer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useProductActionsContext } from '../../../context/ProductActionsProvider';
import { ProductCard } from '../../../ProductCard';
import { TrackingDescriptorType } from '../../../Tracking/types';
import { useProductCompare } from '../../../ProductCompare';
import { useCustomerContext } from '../../../context/CustomerContextProvider';
import { PromoCardCarousel } from '../../../PromoCard/PromoCard';
import { PromoCardTransformer } from '../PromoCardTransformer';
import { UseProductsProps } from '../../../hooks/useProducts/useProducts';
import { Cart } from '@jbhi-fi/lanyard-ui/components/Icons';
import { useEventsContext } from '../../../context/Events';

export type ToProductCarouselBaseOptions = {
  productTrackingData?: {
    descriptor?: TrackingDescriptorType;
    listPrefix?: string;
    trackingListName?: string;
  };
  testId?: string;
};

const getCarouselButton = (
  entry: ContentfulCarousel
): {
  buttonType?: LanyardButtonProps['type'];
  buttonLabel: string;
  buttonUrl?: string;
} => {
  const hasButton = entry?.fields?.buttonType && entry?.fields?.buttonType !== 'None';
  const buttonType =
    entry?.fields?.buttonType?.toLowerCase() === 'green' ? 'action' : entry?.fields?.buttonType?.toLowerCase();
  return {
    buttonType: hasButton ? (buttonType as LanyardButtonProps['type']) : undefined,
    buttonLabel: entry?.fields?.buttonLabel ?? 'View all',
    buttonUrl:
      entry.fields?.productSource === ContentfulProductSource.Collection
        ? CollectionTransformer.toHref(entry?.fields?.collection)
        : entry?.fields?.buttonUrl,
  };
};

export abstract class CarouselTransformer {
  static toPresentationalVideoCardCarouselProps = ({
    fields,
  }: ContentfulCarousel): PresentationVideoCardCarouselProps => {
    const coverImage = fields?.image?.fields ? AssetTransformer.toImageProps(fields.image) : null;

    return {
      cards: fields?.articles
        ?.map(
          article =>
            article?.fields && {
              ...CardTransformer.toCardProps(article),
              isContained: true,
              content: article.fields.author?.fields ? (
                <UserProfile {...AuthorTransformer.toUserProfileProps(article.fields.author)} position={null} />
              ) : null,
              footerProps: undefined,
            }
        )
        .filter(Boolean) as Array<CardGridCardProps>,
      coverContent: (
        <DynamicCarouselCover
          heading={fields?.heading ?? ''}
          content={fields?.subText ?? ''}
          coverImageUrl={buildContentfulApiImgSrc(coverImage?.src ?? '')}
          coverImageAlt={coverImage?.alt ?? ''}
        />
      ),
    };
  };

  static toProductCarouselProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions & {
      isMiniProduct?: boolean;
    } = {}
  ): ProductCarouselProps => {
    const { fields } = entry;
    const isMiniProduct = options.isMiniProduct || entry.fields?.type === ContentfulCarouselType.MiniProduct;
    const trackingListName =
      options.productTrackingData?.trackingListName ||
      (fields?.heading ? `${options.productTrackingData?.listPrefix ?? ''}${fields.heading}` : undefined);
    const { buttonLabel, buttonType, buttonUrl } = getCarouselButton(entry);
    let ctaButton, linkProps;
    if (buttonType && buttonUrl) {
      if (buttonType === 'link') {
        linkProps = {
          href: buttonUrl,
          label: buttonLabel,
        };
      } else {
        ctaButton = (
          <AlignedButton
            alignment="Centred"
            onClick={ButtonTransformer.toClickHandler({
              href: buttonUrl,
            })}
            type={buttonType}
            label={buttonLabel}
            icon={<Cart />}
            iconPosition="left"
          />
        );
      }
    }

    return {
      heading: fields?.heading,
      shouldTrackImpressions: true,
      rewind: !isMiniProduct,
      ...CarouselTransformer.toProductSourceProps(entry),
      ctaButton,
      linkProps,
      productCardSkeletonProps: isMiniProduct ? { variant: 'mini' } : undefined,
      shouldLoadOnScroll: fields?.shouldLoadOnScroll === false ? false : true,
      trackingDescriptor: {
        ecommerceExtraProps: { list: trackingListName },
      },
      renderProduct: isMiniProduct
        ? ({ hit }) => (
            <MiniProductTile
              {...AlgoliaHitTransformer.toMiniProductTileProps(hit, {
                infoVariant: CarouselTransformer._toMiniProductInfoVariant(entry),
              })}
              shouldUseProgressiveImage
            />
          )
        : ({ hit, position }) => (
            <CarouselTransformer.ProductComponent
              hit={hit}
              position={position}
              trackingDescriptor={options?.productTrackingData?.descriptor}
              trackingListPrefix={options?.productTrackingData?.listPrefix}
              trackingListName={trackingListName}
            />
          ),
    };
  };

  static toContentBlockProps = (entry: ContentfulCarousel): Partial<ContentBlockProps> => {
    const baseProps: Partial<ContentBlockProps> = {};

    switch (entry.fields?.type) {
      case ContentfulCarouselType.ProductWithLeadVideo:
        return {
          ...baseProps,
          paddingX: { mobile: 'none', tablet: 8 } as Atoms['paddingX'],
        };
      default:
        return baseProps;
    }
  };

  static toPresentationProductCarouselProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions & { isMiniProduct?: boolean } = {}
  ): PresentationProductCarouselProps => {
    const { fields } = entry;
    const coverImage = fields?.image?.fields ? AssetTransformer.toImageProps(fields.image) : null;
    const shouldShowNumber = fields?.type === ContentfulCarouselType.Chart;
    const isMiniProduct = options.isMiniProduct || entry?.fields?.type === ContentfulCarouselType.MiniProduct;
    const baseProps = CarouselTransformer.toProductCarouselProps(entry, options);
    const { buttonLabel, buttonType, buttonUrl } = getCarouselButton(entry);

    return {
      ...baseProps,
      heading: undefined,
      ctaButton: undefined,
      linkProps: undefined,
      calloutBtn:
        buttonType && buttonUrl ? (
          <Button
            onClick={ButtonTransformer.toClickHandler({
              href: buttonUrl,
            })}
            type={buttonType}
            label={buttonLabel}
            icon={<Cart />}
            iconPosition="left"
          />
        ) : null,
      coverImageSrc: coverImage?.src,
      coverImageAlt: coverImage?.alt,
      coverContent: fields?.heading ? (
        <>
          <DomFont
            as="h2"
            style={{
              color: 'white',
              // @ts-ignore
              margin: `1rem 0 0 0`,
            }}
          >
            {fields.heading}
          </DomFont>
          {fields.subText && entry.fields?.type !== ContentfulCarouselType.Chart ? (
            <p style={{ color: 'white', margin: '8px 0 0 0' }}>{fields.subText}</p>
          ) : null}
        </>
      ) : null,
      renderProduct: isMiniProduct
        ? ({ hit, position }) => {
            return (
              <MiniProductTile
                {...AlgoliaHitTransformer.toMiniProductTileProps(hit, {
                  infoVariant: CarouselTransformer._toMiniProductInfoVariant(entry),
                  number: shouldShowNumber ? position : undefined,
                })}
                shouldUseProgressiveImage
              />
            );
          }
        : ({ hit, position }) => (
            <CarouselTransformer.ProductComponent
              hit={hit}
              position={position}
              trackingDescriptor={options?.productTrackingData?.descriptor}
              trackingListPrefix={options?.productTrackingData?.listPrefix}
              trackingListName={options?.productTrackingData?.trackingListName}
            />
          ),
    };
  };

  static toCarouselLeadBrandProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions = {}
  ): CarouselLeadBrandProps => {
    const productCarouselProps = CarouselTransformer.toProductCarouselProps(entry, options);
    return {
      productCarouselProps: {
        ...productCarouselProps,
        linkProps: undefined,
        heading: undefined,
        ctaButton: undefined,
      },
      heading: entry?.fields?.heading ?? '',
      subText: entry?.fields?.subText,
      brandImage: entry?.fields?.image ? (
        <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry.fields.image)} sizes="312px" />
      ) : null,
      linkProps: productCarouselProps.linkProps,
      calloutBtn: productCarouselProps.ctaButton,
    };
  };

  static toCarouselLeadImageProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions & { shouldHideHeadingOnMobile?: boolean } = {}
  ): CarouselLeadImageProps => {
    const productCarouselProps = CarouselTransformer.toProductCarouselProps(entry, {
      productTrackingData: options.productTrackingData,
    });

    return {
      productCarouselProps: {
        ...productCarouselProps,
        linkProps: undefined,
        heading: undefined,
        ctaButton: undefined,
      },
      linkProps: productCarouselProps.linkProps,
      ctaButton: productCarouselProps.ctaButton,
      desktopImage: entry?.fields?.image ? (
        <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry.fields.image)} sizes="312px" />
      ) : undefined,
      mobileImage: entry?.fields?.mobileImage ? (
        <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry.fields.mobileImage)} sizes="100vw" />
      ) : undefined,
      heading: entry.fields?.heading ?? '',
      subText: entry.fields?.subText,
      shouldHideHeadingOnMobile: options?.shouldHideHeadingOnMobile,
    };
  };

  static toCarouselLeadVideoProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions = {}
  ): CarouselLeadVideoProps => {
    const productCarouselProps = CarouselTransformer.toProductCarouselProps(entry, options);
    return {
      productCarouselProps: {
        ...productCarouselProps,
        linkProps: undefined,
        heading: undefined,
        ctaButton: undefined,
      },
      linkProps: productCarouselProps.linkProps,
      ctaButton: productCarouselProps.ctaButton,
      mobileCoverImage: entry?.fields?.mobileVideoCoverImage ? (
        <ContentfulImage
          {...AssetTransformer.toContentfulImageProps(entry.fields.mobileVideoCoverImage)}
          sizes="100vw"
        />
      ) : undefined,
      desktopCoverImage: entry?.fields?.image ? (
        <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry.fields.image)} sizes="312px" />
      ) : undefined,
      video: entry?.fields?.youTubeLeadVideo,
    };
  };

  static toListingBlockProductCarouselProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions = {}
  ): ListingBlockProps => {
    const linkProps = (() => {
      const { buttonLabel, buttonType, buttonUrl } = getCarouselButton(entry);

      if (buttonType && buttonUrl) {
        return {
          label: buttonLabel,
          href: buttonUrl,
        };
      }
    })();

    return {
      heading: entry.fields?.heading,
      subHeading: undefined,
      linkProps,
      children: (
        <ProductCarousel
          {...CarouselTransformer.toProductCarouselProps(entry, { ...options })}
          heading={undefined}
          ctaButton={undefined}
        />
      ),
    };
  };

  static toListingBlockPromoCardCarouselProps = (
    { fields }: ContentfulCarousel,
    options: { isLoggedIn?: boolean; testId?: string } = {}
  ): ListingBlockProps => {
    return {
      testId: options?.testId,
      heading: fields?.heading,
      children: (
        <PromoCardCarousel
          cards={fields?.promoCards?.map(e => PromoCardTransformer.toPromoCardProps(e, options)) ?? []}
          lockedCardCount={fields?.lockFirstXEntries}
        />
      ),
    };
  };

  static toContentEntryProps = (
    entry: ContentfulCarousel,
    options: ToProductCarouselBaseOptions & { isLoggedIn?: boolean; testId?: string } = {}
  ): ContentEntry => {
    switch (entry.fields?.type) {
      case ContentfulCarouselType.Product:
        return {
          Component: CarouselLeadImage,
          transformer: e => CarouselTransformer.toCarouselLeadImageProps(e, options),
        };
      case ContentfulCarouselType.ProductWithLeadBrand:
        return {
          Component: CarouselLeadBrand,
          transformer: e => CarouselTransformer.toCarouselLeadBrandProps(e, options),
        };
      case ContentfulCarouselType.ProductWithLeadImage:
        return {
          Component: CarouselLeadImage,
          transformer: e => CarouselTransformer.toCarouselLeadImageProps(e, options),
        };
      case ContentfulCarouselType.ProductWithLeadVideo:
        return {
          Component: CarouselLeadVideo,
          transformer: e => CarouselTransformer.toCarouselLeadVideoProps(e, options),
        };
      case ContentfulCarouselType.Merch:
        return {
          Component: PresentationProductCarousel,
          transformer: e => CarouselTransformer.toPresentationProductCarouselProps(e, options),
        };
      case ContentfulCarouselType.Chart:
        return {
          Component: PresentationProductCarousel,
          transformer: e =>
            CarouselTransformer.toPresentationProductCarouselProps(e, { isMiniProduct: true, ...options }),
        };
      case ContentfulCarouselType.Video:
        return {
          Component: PresentationVideoCardCarousel,
          transformer: CarouselTransformer.toPresentationalVideoCardCarouselProps,
        };
      case ContentfulCarouselType.MiniProduct:
        return {
          Component: ProductCarousel,
          transformer: e => CarouselTransformer.toProductCarouselProps(e, { isMiniProduct: true, ...options }),
        };
      case ContentfulCarouselType.PromoCard:
        return {
          Component: ListingBlock,
          transformer: e =>
            CarouselTransformer.toListingBlockPromoCardCarouselProps(e, {
              isLoggedIn: options.isLoggedIn,
              testId: options.testId,
            }),
        };
      default:
        return { Component: Fragment, transformer: () => ({}) };
    }
  };

  private static _toMiniProductInfoVariant = ({ fields }: ContentfulCarousel): MiniProductInfoVariant => {
    switch (fields?.displayInfo) {
      case ContentfulCarouselDisplayInfo.Format:
        return 'Format';
      case ContentfulCarouselDisplayInfo.ReleaseDate:
        return 'Release Date';
      case ContentfulCarouselDisplayInfo.None:
      default:
        return 'None';
    }
  };

  static toProductSourceProps = (entry: ContentfulCarousel): Partial<UseProductsProps> => {
    switch (entry.fields?.productSource) {
      case ContentfulProductSource.Algolia:
        return {
          algoliaQuery: entry.fields?.algoliaFilterQuery,
        };
      case ContentfulProductSource.Products:
        return {
          productVariantIds: entry.fields?.products ?? [],
        };
      case ContentfulProductSource.GoogleRecAi:
        return {
          googleRecAiModel: entry.fields?.googleRecAiModel,
          googleRecNumberOfProducts: entry.fields?.googleRecAiNumberOfProducts,
          source: 'homepage',
        };
      case ContentfulProductSource.RecentlyViewed:
        return {
          isRecentlyViewed: true,
        };
      case ContentfulProductSource.Collection:
        if (entry.fields?.collection?.fields?.template === 'MerchStudio' && entry.fields?.collection?.fields?.handle) {
          return {
            algoliaQuery: buildSearchFilters(`collectionHandles:"${entry.fields.collection.fields.handle}"`),
          };
        }
        return entry.fields?.collection ? CollectionTransformer.toProductSourceProps(entry.fields.collection) : {};
      default:
        return {
          googleRecAiModel: entry.fields?.googleRecAiModel,
          googleRecNumberOfProducts: entry.fields?.googleRecAiNumberOfProducts,
          algoliaQuery: entry.fields?.algoliaFilterQuery,
          productVariantIds: entry.fields?.products,
          source: 'homepage',
        };
    }
  };

  static ProductComponent = ({
    hit,
    position,
    trackingDescriptor: trackingDescriptorProp,
    trackingListName: trackingListNameProp,
    trackingListPrefix,
  }: {
    hit: AlgoliaProductHit;
    position?: number;
    trackingDescriptor?: TrackingDescriptorType;
    trackingListName?: string;
    trackingListPrefix?: string;
  }) => {
    const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
    const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
    const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
    let trackingListName =
      trackingListNameProp || trackingDescriptorProp?.ecommerceExtraProps?.list || 'STACK - Carousel';
    trackingListName &&= trackingListPrefix ?? '';
    const trackingDescriptor: TrackingDescriptorType = {
      page: '',
      group: trackingListName,
      category: trackingListName,
      ...(trackingDescriptorProp ?? {}),
      ecommerceExtraProps: {
        ...(trackingDescriptorProp?.ecommerceExtraProps ?? {}),
        position,
        list: trackingListName,
      },
    };
    const { postEvent } = useEventsContext();

    return (
      <ProductCard
        {...AlgoliaHitTransformer.toProductCardProps(hit, { trackingDescriptor })}
        {...productCartPropsOverrides}
        variant="tablet"
        getIsCompared={isAddedToCompare}
        onCompare={compareHandleClick}
        getShouldShowCompare={shouldShowCompare}
        isLoggedIn={isLoggedIn}
        onUpdateWishlist={handleUpdateWishlist}
        getIsInWishlist={getIsInWishlist}
        onClick={() => {
          postEvent({
            name: 'component.productcard.click',
            data: { sku: hit.sku, queryId: hit.__queryID, position: position },
          });
        }}
        onAddToCart={
          onAddToCart
            ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
            : undefined
        }
        tooltipVariant="small"
      />
    );
  };
}
